import { inject } from '@angular/core';
import {
  callApiMethod,
  setError,
  setLoaded,
  setLoading,
  withCallState,
  withModel,
  withModelCrud,
} from '@kinet-open-network/client/shared/data-access/state';
import { apiFilter } from '@kinet-open-network/shared/data-access/api';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { MoveCustomerFeeData } from '../models/api/move-fee.model';
import { SetInvoicingPeriod } from '../models/api/set-invoicing-period.model';
import { CustomerFee } from '../models/customer-fee.model';
import { CustomerFeesService } from '../services/customer-fees.service';

export const CustomerFeesStore = signalStore(
  withCallState(),
  withModel<CustomerFee>(),
  withModelCrud(CustomerFeesService),
  withState<{ data: CustomerFee[] }>({ data: [] }),
  withMethods((store, service = inject(CustomerFeesService)) => ({
    loadByApId: (customerId?: number | null, apId?: string | null) => {
      if (!customerId || !apId) {
        patchState(store, { data: [] });
        return;
      }

      patchState(store, setLoading());

      return callApiMethod(
        () =>
          service.query({
            filters: apiFilter<CustomerFee>().customerId(customerId).apId(apId),
            perPage: -1,
            sort: ['createdAt'],
          }),
        {
          next: (resp) => patchState(store, { data: resp.data }, setLoaded()),
          error: (err) => patchState(store, setError(err)),
        },
      );
    },
    setFeeActivationDate: (id: string, activationDate: Date | null) => {
      patchState(store, setLoading());
      return callApiMethod(
        () => service.setFeeActivationDate(id, activationDate),
        {
          next: () => {},
          error: (err) => patchState(store, setError(err)),
        },
      );
    },
    setFeeDeactivationDate: (id: string, deactivationDate: Date | null) => {
      patchState(store, setLoading());
      return callApiMethod(
        () => service.setFeeDeactivationDate(id, deactivationDate),
        {
          next: () => patchState(store, setLoaded()),
          error: (err) => patchState(store, setError(err)),
        },
      );
    },
    changeCustomerFeeApId: (id: string, data: MoveCustomerFeeData) => {
      patchState(store, setLoading());
      return callApiMethod(() => service.changeCustomerFeeApId(id, data), {
        next: () => patchState(store, setLoaded()),
        error: (err) => patchState(store, setError(err)),
      });
    },
    setInvoicingPeriod: (id: string, data: SetInvoicingPeriod) => {
      patchState(store, setLoading());
      return callApiMethod(() => service.setInvoicingPeriod(id, data), {
        next: () => patchState(store, setLoaded()),
        error: (err) => patchState(store, setError(err)),
      });
    },
    setInvoiceState: (id: string, invoiceDate: Date | null) => {
      patchState(store, setLoading());
      return callApiMethod(() => service.setInvoiceState(id, invoiceDate), {
        next: () => patchState(store, setLoaded()),
        error: (err) => patchState(store, setError(err)),
      });
    },
    setState: (
      id: string,
      data: Partial<{
        activationDate: Date | null;
        deactivationDate: Date | null;
      }>,
    ) => {
      patchState(store, setLoading());
      return callApiMethod(() => service.updateState(id, data), {
        next: () => patchState(store, setLoaded()),
        error: (err) => patchState(store, setError(err)),
      });
    },
  })),
);
